import { useContext, useRef } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

/**
 * Generic drag-to-reorder list bound to a store array (`dataLocation`). Renders
 * one draggable row per array item, labelled via a `labels` map keyed by the
 * item's first key (the item shape matches a `Switch.options` entry :
 * `{ <type>: {...} }`). On drop it reorders the store array in place and fires
 * the YAML `actions:` via a custom DOM event (default `sortablecommit`) — so the
 * page can persist the new order declaratively (e.g. a `submitData` save).
 *
 * Reordering a store array is stateful + algorithmic (arrayMove) → impossible in
 * declarative YAML ; `@dnd-kit` is already a dependency (cf. BuilderReorderPanel).
 *
 * YAML :
 *   - type: SortableList
 *     dataLocation: ~~.widgets
 *     labels: { kpiRow: "KPIs", revenueChart: "CA/an" }
 *     actions:
 *       - { what: submitData, on: sortablecommit, url: /api/user/prefs, ... }
 */
const SortableRow = ({ id, label }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 shadow-sm"
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
        aria-label="Déplacer"
      >
        <GripVertical size={16} />
      </button>
      <span className="text-sm text-slate-700 truncate">{label}</span>
    </div>
  )
}

export const SortableList = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: [],
    globalDataContext,
    templateContext,
  })
  const items = Array.isArray(formData) ? formData : []
  const labels = resolve(props.labels) || {}
  const commitEvent = resolve(props.commitEvent) || 'sortablecommit'

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )

  // Index-based stable ids for this render (w0..wn).
  const ids = items.map((_, i) => `w${i}`)
  const labelFor = (it) => {
    const key = it && typeof it === 'object' ? Object.keys(it)[0] : ''
    return labels[key] || key
  }

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    const oldIndex = ids.indexOf(active.id)
    const newIndex = ids.indexOf(over.id)
    if (oldIndex < 0 || newIndex < 0) return
    globalDataContext.updateData(arrayMove(items, oldIndex, newIndex), formDataPath)
    // Fire the declarative save action (store already updated).
    containerRef.current?.dispatchEvent(new Event(commitEvent, { bubbles: true }))
  }

  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div ref={containerRef}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-2">
              {items.map((it, i) => (
                <SortableRow key={ids[i]} id={ids[i]} label={labelFor(it)} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </ActionDependant>
  )
}
